"use client";
import { useState, useEffect } from "react";
import { FaGoogle } from "react-icons/fa";
import { IoClose } from "react-icons/io5";
import { MdLogout } from "react-icons/md";

const GTMAccountModal = ({ isOpen, onClose, onAccountSelect, onDisconnect }) => {
    const [accounts, setAccounts] = useState([]);
    const [containers, setContainers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedAccount, setSelectedAccount] = useState(null);
    const [selectedContainer, setSelectedContainer] = useState(null);
    const [saving, setSaving] = useState(false);
    const [disconnecting, setDisconnecting] = useState(false);

    useEffect(() => {
        if (isOpen) {
            fetchAccounts();
        }
    }, [isOpen]);

    const fetchAccounts = async () => {
        setLoading(true);
        try {
            const response = await fetch('/api/gtm/accounts');
            const data = await response.json();
            
            if (data.success) {
                setAccounts(data.accounts || []);
            }
        } catch (error) {
            console.error('Error fetching accounts:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchContainers = async (accountId) => {
        try {
            const response = await fetch(`/api/gtm/accounts/${accountId}/containers`);
            const data = await response.json();
            setContainers(data.containers || []);
        } catch (error) {
            console.error('Error fetching containers:', error);
            setContainers([]);
        }
    };

    const handleAccountSelect = (account) => {
        setSelectedAccount(account);
        setSelectedContainer(null);
        fetchContainers(account.accountId);
    };

    const handleSave = async () => {
        if (!selectedAccount || !selectedContainer) return;

        setSaving(true);
        try {
            const response = await fetch('/api/gtm/accounts', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    accountId: selectedAccount.accountId,
                    containerId: selectedContainer.publicId
                }),
            });

            const data = await response.json();
            
            if (data.success) {
                onAccountSelect({ account: selectedAccount, container: selectedContainer });
                onClose();
            }
        } catch (error) {
            console.error('Error saving account:', error);
        } finally {
            setSaving(false);
        }
    };

    const handleDisconnect = async () => {
        if (!confirm('Are you sure you want to disconnect GTM? This will remove all saved data and you will need to reconnect.')) {
            return;
        }

        setDisconnecting(true);
        try {
            const response = await fetch('/api/integrations/gtm/disconnect', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    hardDelete: true,
                    revokeToken: true
                }),
            });

            const data = await response.json();
            
            if (data.success) {
                if (onDisconnect) onDisconnect();
                onClose();
            } else {
                alert('Failed to disconnect: ' + (data.error || 'Unknown error'));
            }
        } catch (error) {
            console.error('Error disconnecting:', error);
            alert('Failed to disconnect. Please try again.');
        } finally {
            setDisconnecting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                        <FaGoogle className="text-[#4285F4] text-xl" />
                        <h2 className="text-lg font-semibold text-gray-900">
                            Select GTM Account
                        </h2>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                        <IoClose className="w-6 h-6" />
                    </button>
                </div>

                {loading ? (
                    <div className="flex items-center justify-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#284F9E]"></div>
                    </div>
                ) : accounts.length === 0 ? (
                    <div className="text-center py-8">
                        <p className="text-gray-500">No GTM accounts found</p>
                    </div>
                ) : (
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Account</label>
                            <div className="space-y-2 max-h-32 overflow-y-auto">
                                {accounts.map((account) => (
                                    <div
                                        key={account.accountId}
                                        className={`p-2 border rounded cursor-pointer ${
                                            selectedAccount?.accountId === account.accountId
                                                ? 'border-[#284F9E] bg-[#284F9E]/5'
                                                : 'border-gray-200 hover:border-gray-300'
                                        }`}
                                        onClick={() => handleAccountSelect(account)}
                                    >
                                        <p className="font-medium text-gray-600">{account.name}</p>
                                        <p className="text-sm text-gray-500">ID: {account.accountId}</p>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {selectedAccount && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Container</label>
                                <div className="space-y-2 max-h-32 overflow-y-auto">
                                    {containers.map((container) => (
                                        <div
                                            key={container.containerId}
                                            className={`p-2 border rounded cursor-pointer ${
                                                selectedContainer?.containerId === container.containerId
                                                    ? 'border-[#284F9E] bg-[#284F9E]/5'
                                                    : 'border-gray-200 hover:border-gray-300'
                                            }`}
                                            onClick={() => setSelectedContainer(container)}
                                        >
                                            <p className="font-medium text-gray-600">{container.name}</p>
                                            <p className="text-sm text-gray-500">ID: {container.publicId}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                <div className="flex flex-col gap-3 mt-6">
                    {/* Disconnect button */}
                    {!loading && (
                        <button
                            onClick={handleDisconnect}
                            disabled={disconnecting}
                            className="w-full px-4 py-2 text-red-600 border border-red-300 rounded-md hover:bg-red-50 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <MdLogout className="w-4 h-4" />
                            {disconnecting ? 'Disconnecting...' : 'Disconnect GTM'}
                        </button>
                    )}
                    
                    {/* Select buttons */}
                    <div className="flex gap-3">
                        <button
                            onClick={onClose}
                            className="flex-1 px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSave}
                            disabled={!selectedAccount || !selectedContainer || saving}
                            className="flex-1 px-4 py-2 bg-[#284F9E] text-white rounded-md hover:bg-[#1e3a7a] disabled:opacity-50"
                        >
                            {saving ? 'Saving...' : 'Select'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default GTMAccountModal;